import type { Hex } from "viem";

import type { TxStatusSuccessShape } from "../hooks/useStatusPolling.js";

import { useStatusPolling } from "../hooks/useStatusPolling.js";
import styles from "../styles/TxStatusPoller.module.css";

/**
 * Renders the `Query.txStatus` lifecycle for the LAST hash the user
 * signed (LI.FI tracks the bridge/business tx, not the approve).
 * Polling logic lives in `useStatusPolling`; this component is
 * presentation-only.
 *
 * Why 5s × 60 attempts (= 5 min wall): LI.FI's own UI uses similar
 * cadence; faster polling burns the per-key rate-limit budget on
 * staging without giving useful signal.
 */
export interface TxStatusPollerProps {
  readonly endpoint: string;
  readonly apiKey: string;
  readonly fromChainId: number;
  readonly toChainId: number;
  readonly hashes: readonly Hex[];
}

export function TxStatusPoller(props: TxStatusPollerProps): React.JSX.Element {
  const { endpoint, apiKey, fromChainId, toChainId, hashes } = props;
  const lastHash = hashes[hashes.length - 1];
  const { status, attempt, maxAttempts, running, error, restart } =
    useStatusPolling({
      endpoint,
      apiKey,
      fromChainId,
      toChainId,
      hash: lastHash,
    });

  if (lastHash === undefined) return <></>;

  const isCrossChain = fromChainId !== toChainId;
  const success = status?.__typename === "TxStatusSuccess" ? status : undefined;
  const errPayload =
    status?.__typename !== undefined && status.__typename !== "TxStatusSuccess"
      ? status
      : undefined;

  return (
    <section className={styles.card}>
      <header className={styles.header}>
        <h2>Estado on-chain</h2>
        <span className={styles.summary}>
          {isCrossChain
            ? `${fromChainId} → ${toChainId}`
            : `same chain (${fromChainId})`}
          {running && ` · intento ${attempt}/${maxAttempts}`}
        </span>
      </header>

      {error !== undefined && (
        <p className={styles.error}>
          <strong>Error de polling:</strong> {error}
        </p>
      )}

      {success !== undefined && <SuccessDetails success={success} />}

      {errPayload !== undefined && (
        <p className={styles.error}>
          <strong>{errPayload.__typename}:</strong> {errPayload.message}
        </p>
      )}

      {!running && status !== undefined && (
        <button type="button" className={styles.again} onClick={restart}>
          Reanudar polling
        </button>
      )}
    </section>
  );
}

function SuccessDetails({
  success,
}: {
  readonly success: TxStatusSuccessShape;
}): React.JSX.Element {
  return (
    <dl className={styles.details}>
      <div className={styles.row}>
        <dt>Status</dt>
        <dd>
          <StatusBadge status={success.status} />
          {success.substatus !== undefined && (
            <span className={styles.substatus}> · {success.substatus}</span>
          )}
        </dd>
      </div>
      {success.substatusMessage !== undefined && (
        <div className={styles.row}>
          <dt>Detalle</dt>
          <dd>{success.substatusMessage}</dd>
        </div>
      )}
      {success.sending !== undefined && (
        <LegRow
          label={`Sending (chain ${success.sending.chainId})`}
          txHash={success.sending.txHash}
          explorerUrl={success.sending.explorerUrl}
        />
      )}
      {success.receiving !== undefined && (
        <LegRow
          label={`Receiving (chain ${success.receiving.chainId})`}
          txHash={success.receiving.txHash}
          explorerUrl={success.receiving.explorerUrl}
        />
      )}
      {success.tool !== undefined && (
        <div className={styles.row}>
          <dt>Tool</dt>
          <dd>{success.tool}</dd>
        </div>
      )}
    </dl>
  );
}

function LegRow({
  label,
  txHash,
  explorerUrl,
}: {
  readonly label: string;
  readonly txHash: string;
  readonly explorerUrl: string | undefined;
}): React.JSX.Element {
  return (
    <div className={styles.row}>
      <dt>{label}</dt>
      <dd>
        <code>{txHash}</code>
        {explorerUrl !== undefined && (
          <>
            {" "}
            <a href={explorerUrl} target="_blank" rel="noopener noreferrer">
              ↗
            </a>
          </>
        )}
      </dd>
    </div>
  );
}

function StatusBadge({
  status,
}: {
  readonly status: TxStatusSuccessShape["status"];
}): React.JSX.Element {
  switch (status) {
    case "DONE":
      return <span className={styles.badgeDone}>DONE</span>;
    case "FAILED":
      return <span className={styles.badgeFailed}>FAILED</span>;
    case "INVALID":
      return <span className={styles.badgeInvalid}>INVALID</span>;
    case "NOT_FOUND":
      return <span className={styles.badgeWaiting}>NOT_FOUND</span>;
    case "PENDING":
    default:
      return <span className={styles.badgeWaiting}>PENDING</span>;
  }
}
